/**
 * Offline Sync Service - EXPODIRETO Softys
 *
 * Saves registrations to localStorage when offline.
 * When the device goes online, automatically syncs pending records to the server.
 */

import config, { REMOTE_API } from '../config/request.config';

// Sync always targets the REMOTE server so the totem (offline-first)
// sends data to the cloud even when the local backend is also running.
const SYNC_BASE_URL = REMOTE_API;

export interface SyncRecord {
  id: string;
  data: any;
  timestamp: number;
  synced: boolean;
  syncedAt?: number;
}

const QUEUE_KEY = 'softys_pending_sync';
const MAX_SYNCED_HISTORY = 50; // keep at most 50 synced records for reporting

// ─── Queue Management ────────────────────────────────────────────────────────

export const getSyncQueue = (): SyncRecord[] => {
  try {
    const stored = localStorage.getItem(QUEUE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

export const addToSyncQueue = (data: any): string => {
  const queue = getSyncQueue();
  const record: SyncRecord = {
    id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    data,
    timestamp: Date.now(),
    synced: false,
  };
  queue.push(record);
  saveSyncQueue(queue);
  return record.id;
};

const saveSyncQueue = (queue: SyncRecord[]) => {
  // Trim very old synced records to keep localStorage lean
  const cleaned = [
    ...queue.filter((r) => !r.synced),
    ...queue.filter((r) => r.synced).slice(-MAX_SYNCED_HISTORY),
  ];
  localStorage.setItem(QUEUE_KEY, JSON.stringify(cleaned));
};

export const getPendingCount = (): number =>
  getSyncQueue().filter((r) => !r.synced).length;

/**
 * Mark a specific record (by id) as already synced.
 * Call this after a successful direct POST so syncPendingRecords() won't resend it.
 */
export const markRecordSynced = (recordId: string): void => {
  const queue = getSyncQueue();
  const rec = queue.find((r) => r.id === recordId);
  if (rec) {
    rec.synced = true;
    rec.syncedAt = Date.now();
    saveSyncQueue(queue);
  }
};

// ─── Sync Logic ───────────────────────────────────────────────────────────────

export const syncPendingRecords = async (): Promise<{
  synced: number;
  failed: number;
}> => {
  if (!navigator.onLine) return { synced: 0, failed: 0 };

  const queue = getSyncQueue();
  const pending = queue.filter((r) => !r.synced);
  if (pending.length === 0) return { synced: 0, failed: 0 };

  let synced = 0;
  let failed = 0;

  for (const record of pending) {
    try {
      const response = await fetch(
        `${SYNC_BASE_URL}/v1/subscription`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(record.data),
          signal: AbortSignal.timeout(8000),
        }
      );
      if (response.ok) {
        record.synced = true;
        record.syncedAt = Date.now();
        synced++;
      } else {
        failed++;
      }
    } catch {
      // Server not reachable – stop trying until next attempt
      failed++;
      break;
    }
  }

  saveSyncQueue(queue);
  return { synced, failed };
};

// ─── Auto Sync ────────────────────────────────────────────────────────────────

let syncIntervalId: ReturnType<typeof setInterval> | null = null;

export const startAutoSync = (intervalMs = 30000) => {
  if (syncIntervalId) return; // already running

  // Try immediately on start
  syncPendingRecords();

  // Then try periodically
  syncIntervalId = setInterval(() => {
    if (navigator.onLine) syncPendingRecords();
  }, intervalMs);

  // Also try whenever the device comes back online
  window.addEventListener('online', () => syncPendingRecords());
};

export const stopAutoSync = () => {
  if (syncIntervalId) {
    clearInterval(syncIntervalId);
    syncIntervalId = null;
  }
};
